import { storage as localStorage } from '../utils/storage'

/** Timeout duration (in milliseconds) before marking an article as read when tracking visibility */
const USER_HAS_READ_TIMEOUT = 5000
/** Maximum number of read items to store in local storage (FIFO) */
const READ_ITEMS_LIMIT = 500

export const LOCAL_STORAGE_KEY_READ = 'app:readItems'

// Singleton state: in-memory cache initialized once per app session
let readItemsCache: Set<string> | null = null

/**
 * Initializes the read items cache from localStorage
 * Runs exactly once per app session (singleton pattern)
 */
function initializeCache(): Set<string> {
  // Per visitor state, and the cache is module level, so on a server it would
  // be one visitor's read list answering every later request in the container.
  // The server renders an un deranked feed and the browser deranks on mount.
  if (import.meta.server) return new Set()
  if (readItemsCache !== null) {
    return readItemsCache
  }

  const storedValue = localStorage.getItem(LOCAL_STORAGE_KEY_READ)
  if (!storedValue) {
    readItemsCache = new Set()
    return readItemsCache
  }

  try {
    const list = JSON.parse(storedValue)
    readItemsCache = new Set(Array.isArray(list) ? list : [])
  } catch (error) {
    console.error('Failed to parse read items from localStorage:', error)
    readItemsCache = new Set()
  }

  return readItemsCache
}

/**
 * Persists the cache to localStorage with error handling
 * @param cache - Set of read item IDs
 */
function persistCache(cache: Set<string>) {
  try {
    const list = Array.from(cache).slice(-READ_ITEMS_LIMIT)
    localStorage.setItem(LOCAL_STORAGE_KEY_READ, JSON.stringify(list))
  } catch (error) {
    // Handle QuotaExceededError or other storage exceptions
    console.error('Failed to save read items to localStorage:', error)
  }
}

/**
 * Composable for managing article read tracking (time-based engagement)
 *
 * Tracks articles that users have spent time reading (5s+ visibility).
 *
 * Primary API: Use `trackReadItem()` to monitor article visibility duration.
 *
 * @returns Object containing read tracking methods
 *
 * @example
 * ```ts
 * const { trackReadItem, isItemRead } = useReadItems()
 * const visibility = ref('hidden')
 *
 * // Track article based on visibility duration
 * trackReadItem('article-123', visibility)
 *
 * // Check if article has been read
 * if (isItemRead('article-123')) {
 *   console.log('User spent time reading this')
 * }
 * ```
 */
export const useReadItems = () => {
  const cache = initializeCache()

  /**
   * Adds an item to the read list
   *
   * Note: Exported primarily for testing. In application code, use `trackReadItem()`
   * to properly track time-based engagement rather than calling this directly.
   *
   * @param id - Unique identifier of the item to mark as read
   * @internal Exported for testing purposes only. Use `trackReadItem` in application code.
   */
  const addReadItem = (id: string) => {
    if (!cache.has(id)) {
      cache.add(id)

      while (cache.size > READ_ITEMS_LIMIT) {
        const oldest = cache.values().next().value
        if (oldest !== undefined) {
          cache.delete(oldest)
        } else {
          // Absolute safety break to prevent infinite loops if Set behavior is unexpected
          break
        }
      }

      persistCache(cache)
    }
  }

  /**
   * Checks if an item has been marked as read
   * @param id - Unique identifier of the item to check
   * @returns True if the item is in the read list, false otherwise
   */
  const isItemRead = (id: string): boolean => {
    return cache.has(id)
  }

  /**
   * Sorts items by read status (unread first, read last)
   * @param items - Array of items with an `id` property
   * @returns New sorted array
   */
  const sortItemsByReadStatus = <T extends { id: string }>(items: T[]): T[] => {
    return items.slice().sort((a, b) => {
      const aRead = isItemRead(a.id)
      const bRead = isItemRead(b.id)
      return aRead === bRead ? 0 : aRead ? 1 : -1
    })
  }

  /**
   * Tracks an article as read based on visibility duration
   *
   * Starts a 5-second timer when visibility changes to 'visible'.
   * Cancels timer if visibility changes before timeout completes.
   *
   * @param articleId - ID of the article to track
   * @param visibility - Reactive reference to visibility state
   */
  const trackReadItem = (
    articleId: string | undefined,
    visibility: Ref<string>
  ) => {
    let timer: ReturnType<typeof setTimeout> | null = null

    const markAsRead = () => {
      if (articleId) addReadItem(articleId)
    }

    const stopWatch = watch(
      visibility,
      (currentVisibility) => {
        if (currentVisibility !== 'visible' && timer) {
          clearTimeout(timer)
          timer = null
          return
        }
        if (!timer) {
          timer = setTimeout(() => {
            markAsRead()
            timer = null
          }, USER_HAS_READ_TIMEOUT)
        }
      },
      { immediate: true }
    )

    onScopeDispose(() => {
      stopWatch()
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
    })
  }

  return {
    addReadItem,
    isItemRead,
    sortItemsByReadStatus,
    trackReadItem,
  }
}
